/** Expandable menu — desktop dropdown or mobile full-screen drawer. */
import { useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { PenTool, ShieldCheck, Settings, LogOut, Bell, Compass } from 'lucide-react';
import { useAuth } from '@/shared/auth/AuthProvider';
import {
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  useProfile,
} from '@/shared/api/hooks';
import { getNotificationLink } from '@/shared/utils/notificationLinks';
import type { NotificationsResponse } from '@formency/shared';
import { getDisplayName, getInitials } from '@formency/shared';

const EASE = [0.16, 1, 0.3, 1] as const;

function relativeTime(iso: string): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  return `${weeks}w ago`;
}

function MenuLink({
  to,
  onClick,
  icon: Icon,
  children,
  active,
}: {
  to: string;
  onClick: () => void;
  icon?: React.ComponentType<{ size?: number; strokeWidth?: number }>;
  children: React.ReactNode;
  active?: boolean;
}) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="flex items-center gap-2.5 rounded-xl px-2 py-2.5 transition-colors hover:bg-[rgba(0,0,0,0.04)]"
      style={{
        fontSize: 14,
        fontWeight: active ? 700 : 500,
        color: active ? 'var(--color-text)' : 'var(--color-text-secondary, #444)',
      }}
    >
      {Icon && <Icon size={18} strokeWidth={1.75} />}
      {children}
    </Link>
  );
}

function DangerButton({
  onClick,
  icon: Icon,
  children,
}: {
  onClick: () => void;
  icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2.5 rounded-xl px-2 py-2.5 text-left transition-colors hover:bg-[rgba(0,0,0,0.04)]"
      style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-error, #ef4444)' }}
    >
      <Icon size={18} strokeWidth={1.75} />
      {children}
    </button>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="mb-2 px-1 font-semibold uppercase"
      style={{
        fontSize: 11,
        letterSpacing: '0.06em',
        color: 'var(--color-text-muted, #bbb)',
      }}
    >
      {children}
    </p>
  );
}

function Divider() {
  return (
    <div className="my-3" style={{ height: 1, background: 'var(--color-border)', opacity: 0.4 }} />
  );
}

// ─── Desktop Dropdown ────────────────────────────────────────────────────────

function DesktopDropdown({
  open,
  onClose,
  notifData,
  anchorRef,
}: {
  open: boolean;
  onClose: () => void;
  notifData: NotificationsResponse | undefined;
  anchorRef?: React.RefObject<HTMLElement | null>;
}) {
  const { isInstructor, isSupport, signOut } = useAuth();
  const navigate = useNavigate();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const [menuPosition, setMenuPosition] = useState({ top: 78, left: 0 });

  useLayoutEffect(() => {
    if (!open) return;
    const updatePosition = () => {
      const anchorEl = anchorRef?.current;
      const menuWidth = 256;
      const viewportGutter = 12;
      if (!anchorEl) {
        setMenuPosition({
          top: 78,
          left: window.innerWidth - menuWidth - viewportGutter,
        });
        return;
      }

      const rect = anchorEl.getBoundingClientRect();
      const left = Math.min(
        window.innerWidth - menuWidth - viewportGutter,
        Math.max(viewportGutter, rect.right - menuWidth)
      );
      setMenuPosition({
        top: Math.round(rect.bottom + 10),
        left: Math.round(left),
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, anchorRef]);

  const notifications = notifData?.notifications ?? [];
  const unreadCount = notifData?.unreadCount ?? 0;

  const content = (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />

          <motion.div
            className="fixed z-50 w-64 overflow-hidden"
            style={{
              top: menuPosition.top,
              left: menuPosition.left,
              borderRadius: 'var(--radius-xl)',
              background: 'var(--shell-menu-bg)',
              backdropFilter: 'blur(24px) saturate(1.5)',
              WebkitBackdropFilter: 'blur(24px) saturate(1.5)',
              boxShadow:
                '0 8px 32px -4px var(--shell-glass-shadow), 0 2px 8px -2px rgba(0,0,0,0.08)',
              border: '1px solid var(--shell-menu-border)',
            }}
            initial={{ opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.97 }}
            transition={{ duration: 0.2, ease: EASE }}
          >
            {/* Notifications */}
            {notifications.length > 0 && (
              <>
                <div className="px-3 pt-3 pb-1.5">
                  <div className="mb-1.5 flex items-center justify-between px-2">
                    <span
                      className="flex items-center gap-1.5 font-medium"
                      style={{
                        fontSize: 'var(--text-xs)',
                        color: 'var(--color-text-muted)',
                      }}
                    >
                      <Bell size={12} strokeWidth={2} />
                      Notifications
                    </span>
                    {unreadCount > 0 && (
                      <button
                        type="button"
                        onClick={() => markAllRead.mutate()}
                        className="font-medium transition-opacity hover:opacity-70"
                        style={{
                          fontSize: 'var(--text-xs)',
                          color: 'var(--color-accent)',
                        }}
                      >
                        Mark all read
                      </button>
                    )}
                  </div>
                  {notifications.map((n) => (
                    <button
                      key={n.id}
                      type="button"
                      onClick={() => {
                        if (!n.read) markRead.mutate(n.id);
                        const link = getNotificationLink(n);
                        if (link) navigate(link);
                        onClose();
                      }}
                      className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-[rgba(0,0,0,0.04)]"
                    >
                      <div className="min-w-0 flex-1">
                        <p
                          className="truncate"
                          style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}
                        >
                          {n.title}
                        </p>
                        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                          {relativeTime(n.createdAt)}
                        </p>
                      </div>
                      {!n.read && (
                        <span
                          className="h-1.5 w-1.5 shrink-0 rounded-full"
                          style={{ background: 'var(--color-accent)' }}
                        />
                      )}
                    </button>
                  ))}
                </div>
                <div
                  className="mx-3"
                  style={{ height: 1, background: 'var(--color-border)', opacity: 0.5 }}
                />
              </>
            )}

            <div
              className="mx-3"
              style={{ height: 1, background: 'var(--color-border)', opacity: 0.5 }}
            />

            {/* Studio / Admin */}
            {(isInstructor || isSupport) && (
              <>
                <div className="flex flex-col px-3 py-1.5">
                  {isInstructor && (
                    <MenuLink to="/studio" onClick={onClose} icon={PenTool}>
                      Studio
                    </MenuLink>
                  )}
                  {isSupport && (
                    <MenuLink to="/admin" onClick={onClose} icon={ShieldCheck}>
                      Admin
                    </MenuLink>
                  )}
                </div>
                <div
                  className="mx-3"
                  style={{ height: 1, background: 'var(--color-border)', opacity: 0.5 }}
                />
              </>
            )}

            {/* Settings */}
            <div className="flex flex-col px-3 py-1.5">
              <MenuLink to="/settings" onClick={onClose} icon={Settings}>
                Settings
              </MenuLink>
            </div>

            <div
              className="mx-3"
              style={{ height: 1, background: 'var(--color-border)', opacity: 0.5 }}
            />

            {/* Sign out */}
            <div className="px-3 py-1.5 pb-3">
              <DangerButton
                onClick={() => {
                  onClose();
                  void signOut();
                }}
                icon={LogOut}
              >
                Sign out
              </DangerButton>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(content, document.body);
}

// ─── Mobile Drawer ───────────────────────────────────────────────────────────

function MobileDrawer({
  open,
  onClose,
  notifData,
}: {
  open: boolean;
  onClose: () => void;
  notifData: NotificationsResponse | undefined;
}) {
  const { user, isInstructor, isSupport, signOut } = useAuth();
  const { data: profile } = useProfile();
  const navigate = useNavigate();
  const location = useLocation();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const notifications = notifData?.notifications ?? [];
  const unreadCount = notifData?.unreadCount ?? 0;

  const avatarUrl = profile?.avatarUrl ?? null;
  const email = user?.email ?? '';
  const userShape = { name: user?.name ?? null, email: user?.email ?? null };
  const initials = getInitials(userShape);
  const displayName = getDisplayName(userShape);

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 z-40"
            style={{ background: 'rgba(0,0,0,0.3)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />

          {/* Drawer panel */}
          <motion.div
            className="fixed inset-x-4 top-4 z-50 overflow-y-auto"
            style={{
              maxHeight: 'calc(100dvh - 32px)',
              borderRadius: 20,
              background: 'var(--color-surface-solid, #fff)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
              padding: '24px 20px',
            }}
            initial={{ opacity: 0, y: -12, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -12, scale: 0.97 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
          >
            {/* Profile */}
            <div
              className="mb-4 flex items-center gap-3 border-b pb-5"
              style={{ borderColor: 'var(--color-border)' }}
            >
              <div
                className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full font-semibold"
                style={{
                  background: avatarUrl
                    ? 'var(--color-surface-muted, #e5e5e5)'
                    : 'linear-gradient(135deg, #FF9B4A, #FF6EB1, #8A6CF0)',
                  color: avatarUrl ? 'var(--color-accent)' : '#fff',
                  fontSize: 14,
                }}
              >
                {avatarUrl ? (
                  <img src={avatarUrl} alt={displayName} className="h-full w-full object-cover" />
                ) : (
                  initials
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p
                  className="truncate font-semibold"
                  style={{ fontSize: 15, color: 'var(--color-text)' }}
                >
                  {displayName}
                </p>
                <p className="truncate" style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                  {email}
                </p>
              </div>
            </div>

            {/* Navigate */}
            <div className="mb-1">
              <SectionLabel>Navigate</SectionLabel>
              <MenuLink
                to="/discover"
                onClick={onClose}
                icon={Compass}
                active={location.pathname.startsWith('/discover')}
              >
                Discover
              </MenuLink>
            </div>

            <Divider />

            {/* Notifications */}
            {notifications.length > 0 && (
              <>
                <div className="mb-1">
                  <div className="mb-2 flex items-center justify-between px-1">
                    <SectionLabel>Notifications</SectionLabel>
                    {unreadCount > 0 && (
                      <button
                        type="button"
                        onClick={() => markAllRead.mutate()}
                        className="font-medium transition-opacity hover:opacity-70"
                        style={{ fontSize: 11, color: 'var(--color-accent)' }}
                      >
                        Mark all read
                      </button>
                    )}
                  </div>
                  {notifications.map((n) => (
                    <button
                      key={n.id}
                      type="button"
                      onClick={() => {
                        if (!n.read) markRead.mutate(n.id);
                        const link = getNotificationLink(n);
                        if (link) navigate(link);
                        onClose();
                      }}
                      className="flex w-full items-center gap-2.5 rounded-xl px-2 py-2.5 text-left transition-colors hover:bg-[rgba(0,0,0,0.04)]"
                    >
                      <Bell
                        size={18}
                        strokeWidth={1.75}
                        style={{ color: 'var(--color-text-muted)' }}
                      />
                      <div className="min-w-0 flex-1">
                        <p
                          className="truncate"
                          style={{ fontSize: 14, color: 'var(--color-text)' }}
                        >
                          {n.title}
                        </p>
                      </div>
                      <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                        {relativeTime(n.createdAt)}
                      </span>
                    </button>
                  ))}
                </div>
                <Divider />
              </>
            )}

            {/* Studio / Admin */}
            {(isInstructor || isSupport) && (
              <>
                <div className="mb-1">
                  {isInstructor && (
                    <MenuLink to="/studio" onClick={onClose} icon={PenTool}>
                      Studio
                    </MenuLink>
                  )}
                  {isSupport && (
                    <MenuLink to="/admin" onClick={onClose} icon={ShieldCheck}>
                      Admin
                    </MenuLink>
                  )}
                </div>
                <Divider />
              </>
            )}

            {/* Settings + Sign out */}
            <div>
              <MenuLink to="/settings" onClick={onClose} icon={Settings}>
                Settings
              </MenuLink>
              <DangerButton
                onClick={() => {
                  onClose();
                  void signOut();
                }}
                icon={LogOut}
              >
                Sign out
              </DangerButton>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// ─── Exported Component ──────────────────────────────────────────────────────

export function ExpandableMenu({
  open,
  onClose,
  notifData,
  mobile,
  anchorRef,
}: {
  open: boolean;
  onClose: () => void;
  notifData: NotificationsResponse | undefined;
  mobile?: boolean;
  anchorRef?: React.RefObject<HTMLElement | null>;
}) {
  if (mobile) {
    return <MobileDrawer open={open} onClose={onClose} notifData={notifData} />;
  }
  return (
    <DesktopDropdown open={open} onClose={onClose} notifData={notifData} anchorRef={anchorRef} />
  );
}
